import type { FilterFieldConfig } from '@pesto/ui';

import {
  createBooleanColumn,
  createDateColumn,
  createFilterField,
  createSortableColumnId,
  createTextColumn,
  useGenericListConfig,
} from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { generateSelectableFilterOptions } from '@pesto/ui/features/SmartTable/utils/helpers';

import type { DeviceListItem } from '../toDeviceListItem';

import { DeviceType } from '../../../__generated__/graphql/api';

const columnIds = {
  id: 'id',
  deviceType: createSortableColumnId('deviceTypeSelections', 'deviceTypeSortDirection'),
  deviceModel: 'deviceModel',
  osVersion: 'osVersion',
  appVersion: 'appVersion',
  userId: createSortableColumnId('userIdSearch', 'userIdSortDirection'),
  program: 'program',
  lastUsedAt: 'lastUsedAt',
  createdAt: createSortableColumnId('', 'insertedAtSortDirection'),
  isActive: 'isActive',
};

export const useDevicesListConfig = (programFilterFields: FilterFieldConfig[]) => {
  const filterFields = [
    createFilterField('Device type', columnIds.deviceType, {
      options: generateSelectableFilterOptions(DeviceType, false),
    }),
    createFilterField('User ID', columnIds.userId, { placeholder: 'User ID' }),
    ...programFilterFields,
  ];

  const columns = [
    createTextColumn<DeviceListItem>(columnIds.id, 'Device ID', (row) => row.id),
    createTextColumn<DeviceListItem>(columnIds.deviceType, 'Platform', (row) => row.deviceType),
    createTextColumn<DeviceListItem>(columnIds.deviceModel, 'Model', (row) => row.deviceModel),
    createTextColumn<DeviceListItem>(columnIds.osVersion, 'OS', (row) => row.osVersion),
    createTextColumn<DeviceListItem>(columnIds.appVersion, 'App', (row) => row.appVersion),
    createTextColumn<DeviceListItem>(columnIds.userId, 'User ID', (row) => row.userId),
    createTextColumn<DeviceListItem>(columnIds.program, 'Program', (row) => row.programNames.join(', ')),
    createBooleanColumn<DeviceListItem>(columnIds.isActive, 'Active', (row) => row.isActive),
    createDateColumn<DeviceListItem>(columnIds.lastUsedAt, 'Last used', (row) => row.lastUsedAt),
    createDateColumn<DeviceListItem>(
      columnIds.createdAt,
      ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      (row) => row.createdAt,
    ),
  ];

  return useGenericListConfig<DeviceListItem>({
    columns,
    filterFields,
    showActionColumn: true,
    actionButtonText: 'View user',
    pinnedColumns: ['view'],
  });
};
